import { fetchWithAuth, fetchWithTimeout, API_HOST, ApiError, getAuthStore } from './client';
import { Platform } from 'react-native';
import { refreshToken } from './refresh';
import { getUserCredits } from './credits';
import { webImpersonationApi } from './authImpersonation';

// Auth API slice (Perf 3.1.e — store/API split). Smallest slice, needed by the
// auth store. Pulls only the shared chokepoint from `./client`.
export const authApi = {
  refreshToken,
  getUserCredits,
  ghostSignup: async ({ email, password, turnstileToken, referralCode, clientInfo }) => {
    const data = await fetchWithAuth('/json/auth/signup/ghost', {
      method: 'POST',
      body: {
        email: email.trim(),
        password: password.trim(),
        turnstileToken,
        ...(referralCode && { referralCode }),
        ...(clientInfo && { clientInfo }),
      }
    });

    if (data.error) {
      throw new ApiError(data.message || 'Failed to sign up', 400, data);
    }

    return data;
  },

  requestPhoneVerification: async ({ phone }) => {
    const data = await fetchWithAuth('/json/auth/phone/request', {
      method: 'POST',
      body: { phone },
    });
    if (data.error) {
      throw new ApiError(data.message || 'Failed to send verification code', 400, data);
    }
    return data;
  },

  confirmPhoneVerification: async ({ code }) => {
    const data = await fetchWithAuth('/json/auth/phone/verify', {
      method: 'POST',
      body: { code },
    });
    if (data.error) {
      throw new ApiError(data.message || 'Failed to verify phone', 400, data);
    }
    return data;
  },

  getMfaMethods: async (ticket) => {
    return fetchWithAuth('/json/auth/login/mfa/methods', { method: 'POST', body: { ticket } });
  },

  requestMfaEmail: async (ticket) => {
    const data = await fetchWithAuth('/json/auth/login/mfa/email', { method: 'POST', body: { ticket } });
    if (data.error) throw new ApiError(data.message || 'Failed to send email code', 400, data);
    return data;
  },

  // Complete MFA login with ticket + token (authenticator, email, or backup)
  loginMfa: async ({ ticket, token, clientInfo, method }) => {
    const data = await fetchWithAuth('/json/auth/login/mfa', {
      method: 'POST',
      body: { ticket, token, ...(method && { method }), ...(clientInfo && { clientInfo }) },
    });

    if (data.error) {
      throw new ApiError(data.message || 'Failed to complete MFA login', 400, data);
    }

    return data;
  },

  getCurrentUser: async () => {
    const response = await fetchWithAuth('/json/user/me');
  
    if (response.error) {
      throw new Error(response.message || 'Failed to fetch user data');
    }
    
    return response;
  },

  getCurrentUserSlim: async () => {
    const response = await fetchWithAuth('/json/user/me/slim');
  
    if (response.error) {
      throw new Error(response.message || 'Failed to fetch user data');
    }
    
    return response;
  },

  resendVerificationEmail: async () => {
    const data = await fetchWithAuth('/json/account/resend-verify-email', {
      method: 'POST'
    });
    
    if (data.error) {
      throw new ApiError(data.message || 'Failed to send verification email', 400, data);
    }
    
    return data;
  },

  logout: async ({ pushToken, token, refreshToken } = {}) => {
    // Use only the captured departing credential. The store may already hold
    // another account; never refresh or attach that account's cookies/token.
    if (!token && !refreshToken) return;
    const path = refreshToken ? '/json/auth/logout-session' : '/json/auth/logout';
    const response = await fetchWithTimeout(`${API_HOST}${path}`, {
      method: 'POST', credentials: 'omit',
      headers: { ...(!refreshToken && token ? { Authorization: `Bearer ${token}` } : {}), 'Content-Type': 'application/json', 'X-Platform': Platform.OS },
      body: JSON.stringify({ ...(pushToken ? { pushToken } : {}), ...(refreshToken ? { refreshToken } : {}) }),
    }, 5000);
    if (response.status === 401) return;
    if (!response.ok) throw new ApiError('Server logout cleanup failed', response.status);
  },

  ...webImpersonationApi,
  // ... other auth endpoints
};
